import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import SeatMap from "@/components/SeatMap";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Ticket as TicketIcon, Check, Home, User, IdCard, Sparkles, Clock, MapPin } from "lucide-react";
import CinemaBrand from "@/components/CinemaBrand";
import IdleGuard from "@/components/IdleGuard";
import KioskStyles from "./_kioskTicketStyles";

/**
 * Premium Cinema-Automat — Vorhang-Enthüllung, goldene Marquee-Lichter, Ticket-Stub-Bestätigung.
 *   Schritt 1: Sitzplatz-Auswahl (großer Touch-Seatmap)
 *   Schritt 2: Name + optionale E-Mail
 *   Schritt 3: Bestätigung mit QR-Code + Ticket-Stub-Look
 * Idle-Timeout: 90 s Aktivität → 20 s Warn-Modal → zurück zum Attract-Screen.
 */
export default function KioskTicket() {
  const { showtimeId } = useParams();
  const nav = useNavigate();
  const [st, setSt] = useState(null);
  const [step, setStep] = useState(1); // 1..3
  const [seats, setSeats] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [memberNumber, setMemberNumber] = useState("");
  const [member, setMember] = useState(null); // { id, first_name, last_name, tier, member_number }
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // { tickets, cancel_token }
  const [curtainOpen, setCurtainOpen] = useState(false);

  useEffect(() => {
    // Vorhang enthüllt sich ~1 s nach Mount — Kino-Feeling beim Betreten
    const t = setTimeout(() => setCurtainOpen(true), 900);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => { api.get(`/showtimes/${showtimeId}`).then((r) => setSt(r.data)).catch(() => {}); }, [showtimeId]);
  useEffect(() => {
    const trigger = () => {
      document.documentElement.requestFullscreen?.().catch(() => {});
      window.removeEventListener("touchstart", trigger);
      window.removeEventListener("click", trigger);
    };
    window.addEventListener("touchstart", trigger, { once: true, passive: true });
    window.addEventListener("click", trigger, { once: true });
    return () => {
      window.removeEventListener("touchstart", trigger);
      window.removeEventListener("click", trigger);
    };
  }, []);

  // Alte Idle-Logik entfernt — <IdleGuard /> übernimmt.

  const price = useMemo(() => (st?.ticket_price || 0) * seats.length, [st, seats]);

  const submit = async () => {
    if (!seats.length) return toast.error("Bitte mindestens einen Platz wählen");
    if (!name.trim()) return toast.error("Bitte Name eingeben");
    setBusy(true);
    try {
      const { data } = await api.post("/reservations", {
        showtime_id: showtimeId,
        seats,
        guest_name: name,
        guest_email: email || null,
        guest_phone: null,
        invite_code: null,
        member_number: memberNumber.trim() || null,
        snacks: [],
      });
      setConfirmed({ tickets: data.tickets || [], cancel_token: data.reservation?.cancel_token });
      setStep(3);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitAndPay = async () => {
    if (!seats.length) return toast.error("Bitte mindestens einen Platz wählen");
    if (!name.trim()) return toast.error("Bitte Name eingeben");
    setBusy(true);
    try {
      const { data } = await api.post("/reservations", {
        showtime_id: showtimeId, seats,
        guest_name: name, guest_email: email || null, guest_phone: null, invite_code: null,
        member_number: memberNumber.trim() || null, snacks: [],
      });
      const resId = data.reservation?.id;
      if (!resId) throw new Error("Reservierung ohne ID");
      const { data: chk } = await api.post(`/reservations/${resId}/stripe-checkout`, {
        origin_url: window.location.origin,
      });
      if (chk.checkout_url) { window.location.href = chk.checkout_url; return; }
      toast.error("Stripe-Session konnte nicht erzeugt werden");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler beim Öffnen der Online-Zahlung");
    } finally { setBusy(false); }
  };

  if (!st) return (
    <div className="min-h-screen bg-black flex items-center justify-center text-[#A8A398]">
      <KioskStyles />
      <div className="text-center">
        <div className="marquee-bulbs mb-8"><span/><span/><span/><span/><span/><span/><span/></div>
        <div className="font-heading text-3xl text-[#D4AF37]">Vorstellung wird geladen …</div>
      </div>
    </div>
  );

  const m = st.movie || {};
  const backdrop = m.backdrop_url || m.poster_url;

  return (
    <div className="min-h-screen w-full bg-black text-[#F5F0E6] flex flex-col relative overflow-hidden kiosk-root">
      <KioskStyles />
      {/* Nach 90 s Inaktivität → 20 s Warn-Modal → zurück zum Kiosk-Start */}
      <IdleGuard idleMs={90_000} graceMs={20_000} onExpire={() => nav("/kiosk")} testId="kiosk-ticket-idle" />

      {/* Kino-Ambiance: Film-Backdrop mit Verlauf */}
      {backdrop && (
        <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden>
          <img src={backdrop} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.75) 55%, #000 100%)" }} />
        </div>
      )}

      {/* Roter Samt-Vorhang: enthüllt sich beim Mount */}
      <div className={`kiosk-curtain ${curtainOpen ? "kiosk-curtain-open" : ""}`} aria-hidden>
        <div className="kiosk-curtain-panel kiosk-curtain-left" />
        <div className="kiosk-curtain-panel kiosk-curtain-right" />
      </div>

      {/* Header mit Marquee-Lichterkette */}
      <header className="relative z-10 border-b-2 border-[#D4AF37]/70 px-8 pt-3 pb-5 bg-black/70 backdrop-blur">
        <div className="marquee-bulbs mb-3"><span/><span/><span/><span/><span/><span/><span/><span/><span/><span/><span/><span/></div>
        <div className="flex items-center justify-between gap-4">
          <button onClick={() => nav("/kiosk")} data-testid="kiosk-home-btn" className="flex items-center gap-2 px-5 py-3 border border-[#2A2A2A] rounded-full text-xs uppercase tracking-[0.3em] hover:border-[#D4AF37] hover:text-[#D4AF37] transitions-safe">
            <Home size={14} /> Start
          </button>
          <CinemaBrand size="md" stacked={false} />
          <StepPills current={step} />
        </div>
      </header>

      {/* Sub-Header: Film-Info als Marquee-Karte */}
      <div className="relative z-10 px-8 py-6 border-b border-[#1A1A1A] flex items-center gap-6 bg-black/50 backdrop-blur">
        {m.poster_url && (
          <div className="relative">
            <img src={m.poster_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-20 md:w-28 aspect-[2/3] object-cover rounded shadow-2xl border border-[#D4AF37]/30" />
            <div className="absolute -inset-1 rounded pointer-events-none" style={{ boxShadow: "0 0 30px rgba(212,175,55,0.25)" }} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] flex items-center gap-2"><TicketIcon size={12} /> Heutige Vorstellung</div>
          <div className="font-heading text-3xl md:text-5xl truncate mt-1 gold-shimmer">{m.title_de || m.title_original}</div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#A8A398]">
            <span className="flex items-center gap-1.5"><Clock size={13} className="text-[#D4AF37]/70" /> {fmtDate(st.date)} · <span className="text-[#F5F0E6] font-mono ml-1">{st.start_time}</span></span>
            <Dot /><span className="flex items-center gap-1.5"><MapPin size={13} className="text-[#D4AF37]/70" /> Saal {st.hall}</span>
            {m.duration_min > 0 && (<><Dot /><span>{m.duration_min} Min</span></>)}
            {m.age_rating && (<><Dot /><span className="border border-[#D4AF37]/60 rounded px-1.5 py-0.5 text-[10px] font-mono">FSK {m.age_rating}</span></>)}
          </div>
        </div>
        <div className="text-right hidden md:block">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">Preis / Platz</div>
          <div className="font-heading text-3xl text-[#D4AF37]">{(st.ticket_price || 0).toFixed(2)} €</div>
        </div>
      </div>

      {/* Main */}
      <main className="relative z-10 flex-1 overflow-auto p-8">
        {step === 1 && (
          <StepSeats st={st} seats={seats} setSeats={setSeats} />
        )}
        {step === 2 && (
          <StepGuest name={name} setName={setName} email={email} setEmail={setEmail} memberNumber={memberNumber} setMemberNumber={setMemberNumber} member={member} setMember={setMember} />
        )}
        {step === 3 && confirmed && (
          <StepConfirm confirmed={confirmed} st={st} price={price} />
        )}
      </main>

      {/* Footer bar */}
      <footer className="relative z-10 border-t-2 border-[#D4AF37]/70 px-8 py-5 flex items-center justify-between bg-black/85 backdrop-blur gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">Auswahl</div>
          <div className="font-heading text-xl truncate">
            {step === 3 ? "Ticket erstellt" : (seats.length ? `${seats.length} Platz${seats.length === 1 ? "" : "e"} · ${seats.join(", ")}` : "Bitte Platz wählen")}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">Gesamt</div>
          <div className="font-heading text-3xl text-[#D4AF37]" data-testid="kiosk-total">{price.toFixed(2)} €</div>
        </div>
        <div className="flex items-center gap-3">
          {step > 1 && step < 3 && (
            <button onClick={() => setStep((s) => s - 1)} data-testid="kiosk-back-btn"
              className="min-h-[64px] px-6 py-4 border border-[#2A2A2A] rounded-full text-xs uppercase tracking-[0.35em] hover:border-[#D4AF37] flex items-center gap-2">
              <ChevronLeft size={16} /> Zurück
            </button>
          )}
          {step === 1 && (
            <button onClick={() => seats.length > 0 && setStep(2)} disabled={!seats.length} data-testid="kiosk-next-seats"
              className="min-h-[64px] px-8 py-4 rounded-full text-sm uppercase tracking-[0.35em] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed bg-[#D4AF37] text-black hover:bg-[#EEDC82]">
              Weiter <ChevronRight size={18} />
            </button>
          )}
          {step === 2 && (
            <div className="flex gap-2 flex-wrap justify-end">
              <button onClick={submit} disabled={busy || !name.trim()} data-testid="kiosk-submit-btn"
                className="min-h-[64px] px-6 py-4 rounded-full text-sm uppercase tracking-[0.3em] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
                {busy ? "…" : (<>An Kasse zahlen <TicketIcon size={16} /></>)}
              </button>
              <button onClick={submitAndPay} disabled={busy || !name.trim()} data-testid="kiosk-stripe-btn"
                className="min-h-[64px] px-8 py-4 rounded-full text-sm uppercase tracking-[0.3em] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed bg-[#D4AF37] text-black hover:bg-[#EEDC82]">
                {busy ? "Öffne Stripe …" : (<>Jetzt zahlen · Karte <ChevronRight size={18} /></>)}
              </button>
            </div>
          )}
          {step === 3 && (
            <button onClick={() => nav("/kiosk")} data-testid="kiosk-finish-btn"
              className="min-h-[64px] px-8 py-4 rounded-full text-sm uppercase tracking-[0.35em] flex items-center gap-2 bg-[#D4AF37] text-black hover:bg-[#EEDC82]">
              <Check size={18} /> Fertig
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

/* ---------- Steps ---------- */
function StepPills({ current }) {
  const items = [
    { n: 1, l: "Sitzplatz" },
    { n: 2, l: "Name" },
    { n: 3, l: "Bestätigung" },
  ];
  return (
    <div className="hidden md:flex items-center gap-3">
      {items.map((it) => (
        <div key={it.n} className={`px-4 py-2 rounded-full border text-[10px] uppercase tracking-[0.35em] ${current === it.n ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : current > it.n ? "border-[#D4AF37]/50 text-[#D4AF37]/70" : "border-[#2A2A2A] text-[#A8A398]"}`}>
          <span className="font-mono mr-2">{it.n}.</span>{it.l}
        </div>
      ))}
    </div>
  );
}

function StepSeats({ st, seats, setSeats }) {
  return (
    <section className="max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Schritt 1</div>
        <h2 className="font-heading text-4xl md:text-5xl mt-2">Sitzplatz wählen</h2>
        <div className="text-[#A8A398] text-sm mt-2">Tippen zum Auswählen. Nochmals tippen zum Abwählen.</div>
      </div>
      <div className="card-lux p-6 md:p-8 kiosk-seatmap">
        <SeatMap
          rows={st.seat_plan?.rows || []}
          seatTypes={st.seat_plan?.seat_types || []}
          seatStatus={st.seat_status || {}}
          selected={seats}
          onToggle={(id) => setSeats((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])}
          decorations={st.seat_plan?.decorations || []}
          backgroundUrl={st.seat_plan?.background_url || ""}
          backgroundOpacity={st.seat_plan?.background_opacity ?? 0.35}
          layers={st.seat_plan?.layers || []}
          freeMode={!!st.seat_plan?.free_mode}
          curve={st.seat_plan?.default_curve ?? 8}
        />
      </div>
      {/* Größere Sitze für Touch */}
      <style>{`
        .kiosk-seatmap .seat { min-width: 48px !important; min-height: 48px !important; font-size: 14px !important; }
        .kiosk-seatmap .seat.gap { min-width: 24px !important; min-height: 48px !important; }
      `}</style>
    </section>
  );
}

function StepGuest({ name, setName, email, setEmail, memberNumber, setMemberNumber, member, setMember }) {
  return (
    <section className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Schritt 2</div>
        <h2 className="font-heading text-4xl md:text-5xl mt-2">Wer schaut mit?</h2>
        <div className="text-[#A8A398] text-sm mt-2">Mitglied? Einfach Kundennummer eingeben — der Rest wird automatisch ausgefüllt.</div>
      </div>

      <div className="card-lux p-8 space-y-6">
        <MemberLookupField
          value={memberNumber}
          onChange={setMemberNumber}
          member={member}
          setMember={setMember}
          setName={setName}
          setEmail={setEmail}
        />
        <BigField icon={<User size={18} />} label="Name" value={name} onChange={setName} placeholder="Vor- und Nachname" testid="kiosk-name-input" autoFocus={!member} />
        <BigField label="E-Mail (optional)" value={email} onChange={setEmail} placeholder="Für Ticket-Zusendung" type="email" testid="kiosk-email-input" />
        <div className="text-xs text-[#A8A398] flex items-center gap-2 pt-2">
          <div className="w-2 h-2 rounded-full bg-[#D4AF37]" /> Zahlung an der Kasse — das Ticket ist reserviert, sobald du auf „Ticket kaufen" tippst.
        </div>
      </div>
    </section>
  );
}

const TIER_LABEL = { bronze: "Bronze", silver: "Silver", gold: "Gold", vip: "VIP" };
const TIER_COLOR = { bronze: "#B87333", silver: "#C0C0C0", gold: "#D4AF37", vip: "#EEDC82" };

function MemberLookupField({ value, onChange, member, setMember, setName, setEmail }) {
  const [busy, setBusy] = useState(false);
  const lookup = async () => {
    const q = value.trim();
    if (!q) return toast.error("Bitte Kundennummer eingeben");
    setBusy(true);
    try {
      const { data } = await api.get(`/club/by-qr/${encodeURIComponent(q)}`);
      setMember(data);
      const full = `${data.first_name || ""} ${data.last_name || ""}`.trim();
      if (full) setName(full);
      if (data.email) setEmail(data.email);
      toast.success(`Willkommen zurück, ${data.first_name || "Mitglied"}!`);
    } catch (e) {
      toast.error("Keine Kundennummer gefunden");
      setMember(null);
    } finally { setBusy(false); }
  };
  const clear = () => { setMember(null); onChange(""); };
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-2 flex items-center gap-2">
        <IdCard size={18} /> Kundennummer <span className="text-[#575148]">(optional)</span>
      </div>
      {member ? (
        <div className="w-full bg-[#050505] border-2 rounded-md px-5 py-5 flex items-center justify-between" style={{ borderColor: TIER_COLOR[member.tier] || "#D4AF37" }} data-testid="kiosk-member-card">
          <div className="flex items-center gap-4">
            <Sparkles size={28} style={{ color: TIER_COLOR[member.tier] || "#D4AF37" }} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em]" style={{ color: TIER_COLOR[member.tier] || "#D4AF37" }}>
                {TIER_LABEL[member.tier] || "Mitglied"} · Nr. {member.member_number || value}
              </div>
              <div className="font-heading text-2xl mt-1">{member.first_name} {member.last_name}</div>
            </div>
          </div>
          <button onClick={clear} data-testid="kiosk-member-clear"
            className="text-[10px] uppercase tracking-[0.28em] px-3 py-2 rounded-full border border-[#2A2A2A] text-[#A8A398] hover:border-[#7A1921] hover:text-[#EE9CA1]">
            × Entfernen
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            data-testid="kiosk-member-input"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
            placeholder="z.B. AB12CD"
            className="flex-1 bg-[#050505] border-2 border-[#2A2A2A] rounded-md px-5 py-5 text-2xl font-mono uppercase tracking-widest outline-none focus:border-[#D4AF37] placeholder-[#575148]"
          />
          <button onClick={lookup} disabled={busy || !value.trim()} data-testid="kiosk-member-lookup"
            className="px-8 py-5 bg-[#D4AF37] hover:bg-[#EEDC82] text-black font-medium tracking-widest uppercase text-sm rounded-md disabled:opacity-50 transitions-safe">
            {busy ? "…" : "Prüfen"}
          </button>
        </div>
      )}
      {!member && (
        <div className="text-[10px] text-[#575148] mt-2">
          Keine Mitgliedschaft? Einfach unten Name eingeben.
        </div>
      )}
    </label>
  );
}

function BigField({ icon, label, value, onChange, placeholder, type = "text", testid, autoFocus }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mb-2 flex items-center gap-2">{icon}{label}</div>
      <input
        data-testid={testid}
        autoFocus={autoFocus}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[#050505] border-2 border-[#2A2A2A] rounded-md px-5 py-5 text-2xl font-heading outline-none focus:border-[#D4AF37] placeholder-[#575148]"
      />
    </label>
  );
}

function StepConfirm({ confirmed, st, price }) {
  const t = confirmed.tickets?.[0];
  const m = st.movie || {};
  const qrValue = confirmed.cancel_token
    ? `${window.location.origin}/reservierung/${confirmed.cancel_token}`
    : (t?.qr_token ? `${window.location.origin}/ticket/${t.qr_token}` : "");

  return (
    <section className="max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-3 px-4 py-2 border border-[#D4AF37]/60 rounded-full">
          <Check size={16} className="text-[#D4AF37]" />
          <span className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Bestätigt</span>
        </div>
        <h2 className="font-heading text-4xl md:text-5xl mt-4">Viel Spaß im {st.hall}</h2>
        <div className="text-[#A8A398] mt-2">Bitte an der Kasse zahlen und Ticket abholen.</div>
      </div>

      <div className="card-lux p-8 grid md:grid-cols-[1fr_auto] gap-8 items-center gold-glow">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">Ticket</div>
          <div className="font-heading text-3xl md:text-4xl">{m.title_de || m.title_original}</div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <D label="Datum" v={fmtShort(st.date)} />
            <D label="Start" v={st.start_time} />
            <D label="Saal" v={st.hall} />
            <D label="Plätze" v={(confirmed.tickets || []).map((x) => `${x.row}${x.seat}`).join(", ")} />
          </div>
          <div className="mt-6 flex items-center justify-between border-t border-[#1A1A1A] pt-4">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#A8A398]">Gesamt</div>
            <div className="font-heading text-3xl text-[#D4AF37]">{price.toFixed(2)} €</div>
          </div>
        </div>
        <div className="text-center">
          {qrValue && (
            <div className="bg-white p-4 rounded inline-block">
              <QRCodeCanvas value={qrValue} size={180} />
            </div>
          )}
          <div className="mt-3 text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Zum Einlass scannen</div>
          {t?.id && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tickets/${t.id}/pdf`} target="_blank" rel="noreferrer"
              className="mt-3 inline-block text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] underline">
              PDF öffnen
            </a>
          )}
        </div>
      </div>

      <div className="mt-6 text-center text-xs text-[#A8A398]">
        Bitte zeige den QR-Code an der Kasse für die Bezahlung. Danach ist dein Sitzplatz gesichert.
      </div>
    </section>
  );
}

function D({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">{label}</div>
      <div className="font-mono text-xl">{v || "—"}</div>
    </div>
  );
}
function Dot() { return <span className="w-1 h-1 rounded-full bg-[#D4AF37]/60" />; }
function fmtDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}
function fmtShort(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}
